<template>
  <v-container>
    <ContentHeader
      header-icon="mdi-check-decagram-outline"
      header-title="Standards"
    >
    </ContentHeader>
    <pt-sheet>
      <h1 class="mb-4">Overlay Messaging</h1>
      <h2 class="h3-desktop-regular-20 mb-4">
        Overlay messages appear top center over the application. If you scroll
        the page, the message will stay in its position relative to the browser
        window. The message will auto dismiss after a delay of 30 seconds by
        default. This time can be customized.
      </h2>
      <v-card outlined>
        <v-card-title class="py-2">
          <v-spacer></v-spacer>
          <v-btn icon @click="showCode1 = !showCode1">
            <v-icon>mdi-code-tags</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider v-if="showCode1" />
        <v-expand-transition>
          <v-card-text v-if="showCode1" class="grey lighten-5">
            <code-block :code="overlayMessagingCode" language="markup" />
          </v-card-text>
        </v-expand-transition>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="sendNotification('info')">
            Open Overlay Info Message
          </v-btn>
          <v-btn @click="sendNotification('success')">
            Open Overlay Success Message
          </v-btn>
          <v-btn @click="sendNotification('warning')">
            Open Overlay Warning Message
          </v-btn>
          <v-btn @click="sendNotification('error')">
            Open Overlay Error Message
          </v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </pt-sheet>
    <pt-sheet class="mt-12">
      <h1 class="mb-4">Banner Messaging</h1>
      <h2 class="h3-desktop-regular-20 mb-4">
        Banner messages appear prominently above the content they should be
        associated with. The messages are persistent unless manually cleared.
      </h2>
      <v-card outlined>
        <v-card-title class="py-2">
          <v-spacer></v-spacer>
          <v-btn icon @click="showCode2 = !showCode2">
            <v-icon>mdi-code-tags</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider v-if="showCode2" />
        <v-expand-transition>
          <v-card-text v-if="showCode2" class="grey lighten-5">
            <code-block :code="bannerMessagingCode" language="markup" />
          </v-card-text>
        </v-expand-transition>
        <v-divider />
        <v-card-text>
          <message-container :messages.sync="alerts1"></message-container>
        </v-card-text>
      </v-card>
      <h2 class="h3-desktop-regular-20 mb-4 mt-12">
        Click the button to see a banner message in a dialog.
      </h2>
      <v-card outlined>
        <v-card-title class="py-2">
          <v-spacer></v-spacer>
          <v-btn icon @click="showCode3 = !showCode3">
            <v-icon>mdi-code-tags</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider v-if="showCode3" />
        <v-expand-transition>
          <v-card-text v-if="showCode3" class="grey lighten-5">
            <code-block :code="bannerMessagingDialogCode" language="markup" />
          </v-card-text>
        </v-expand-transition>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-dialog v-model="showBannerMessagingDialog" width="600">
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on">
                Show Message in Dialog
              </v-btn>
            </template>
            <v-container>
              <v-card>
                <v-card-title>
                  <v-spacer />
                  <v-btn icon @click="showBannerMessagingDialog = false">
                    <v-icon>mdi-window-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="px-8">
                  <v-form>
                    <message-container
                      :messages.sync="alerts2"
                      class="mb-8"
                    ></message-container>
                    <v-text-field outlined label="First Name" />
                    <v-text-field outlined label="Last Name" />
                  </v-form>
                </v-card-text>
                <v-card-actions class="pa-8">
                  <v-spacer />
                  <v-btn @click="addMessage2('info')">
                    Add Info
                  </v-btn>
                  <v-btn @click="addMessage2('success')">
                    Add Success
                  </v-btn>
                  <v-btn @click="addMessage2('warning')">
                    Add Warning
                  </v-btn>
                  <v-btn @click="addMessage2('error')">
                    Add Error
                  </v-btn>
                  <v-btn @click="clearMessages2">
                    Clear Messages
                  </v-btn>
                  <v-spacer />
                </v-card-actions>
              </v-card>
            </v-container>
          </v-dialog>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </pt-sheet>
    <pt-sheet class="mt-12">
      <h1 class="mb-4">Inline Messaging</h1>
      <h2 class="h3-desktop-regular-20 mb-4">
        Inline messages are brief, in-context messages that tell merchants
        something about a single input in a form. Use inline messaging to help
        merchants understand why a form input may not be valid and how to fix
        it.
      </h2>
      <v-alert type="info">
        Click
        <a
          href="https://v2.vuetifyjs.com/en/components/text-fields/#validation"
          target="_blank"
        >
          here</a
        >
        to view Vuetify's standard form validation documentation for inline
        messaging.
      </v-alert>
      <v-card outlined>
        <v-card-title class="py-2">
          <v-spacer></v-spacer>
          <v-btn icon @click="showCode4 = !showCode4">
            <v-icon>mdi-code-tags</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider v-if="showCode4" />
        <v-expand-transition>
          <v-card-text v-if="showCode4" class="grey lighten-5">
            <code-block :code="inlineMessagingCode" language="markup" />
          </v-card-text>
        </v-expand-transition>
        <v-divider />
        <v-card-text>
          <h2 class="h3-desktop-regular-20 mb-4">
            Example of server side validation using inline messaging via
            Vuetify's standard form validation.
          </h2>
          <v-card flat>
            <v-row>
              <v-spacer />
              <v-col cols="6">
                <v-form @submit.prevent="showServerSideErrors">
                  <v-text-field
                    outlined
                    label="Username"
                    v-model="username"
                    :error-messages="usernameErrors"
                    :success-messages="usernameSuccess"
                  ></v-text-field>
                  <v-text-field
                    outlined
                    label="Email"
                    v-model="email"
                    :error-messages="emailErrors"
                    :success-messages="emailSuccess"
                  ></v-text-field>

                  <v-btn type="submit" class="mr-4">Show Server Errors</v-btn>
                  <v-btn @click="showServerSuccessMessages" class="mr-4"
                    >Show Server Success Message</v-btn
                  >
                  <v-btn @click="clearServerSideErrors">Clear Messages</v-btn>
                </v-form>
              </v-col>
              <v-spacer />
            </v-row>
          </v-card>
        </v-card-text>
      </v-card>
    </pt-sheet>
  </v-container>
</template>

<script>
import ContentHeader from "@/views/ContentHeader.vue";
import PtSheet from "@/components/PtSheet.vue";
import hasNotifications from "@/mixins/has-notifications";
import MessageContainer from "@/components/MessageContainer.vue";
import CodeBlock from "@/pds/CodeBlock.vue";
import OverlayMessagingCode from "@/pds/snippets/OverlayMessagingCode.txt";
import BannerMessagingCode from "@/pds/snippets/BannerMessagingCode.txt";
import BannerMessagingDialogCode from "@/pds/snippets/BannerMessagingDialogCode.txt";
import InlineMessagingCode from "@/pds/snippets/InlineMessagingCode.txt";

export default {
  name: "Standards",
  components: {
    CodeBlock,
    MessageContainer,
    PtSheet,
    ContentHeader
  },
  data() {
    return {
      showCode1: false,
      showCode2: false,
      showCode3: false,
      showCode4: false,
      showBannerMessagingDialog: false,
      overlayMessagingCode: OverlayMessagingCode,
      bannerMessagingCode: BannerMessagingCode,
      bannerMessagingDialogCode: BannerMessagingDialogCode,
      inlineMessagingCode: InlineMessagingCode,
      alerts1: [],
      alerts2: [],
      username: "",
      email: "",
      usernameErrors: [],
      emailErrors: [],
      usernameSuccess: [],
      emailSuccess: []
    };
  },
  mixins: [hasNotifications],
  methods: {
    sendNotification(type) {
      this.showMessage({
        text: `This is an overlay message of type ${type}. It will auto dismiss in 30 seconds.`,
        mode: type,
        timeout: 30000
      });
    },
    addMessage1(type) {
      this.alerts1.push({
        text: `This is a banner message of type ${type}.`,
        type: type
      });
    },
    addMessage2(type) {
      this.alerts2.push({
        text: `This is a banner message of type ${type}.`,
        type: type
      });
    },
    clearMessages2() {
      this.alerts2 = [];
    },
    showServerSideErrors() {
      this.usernameErrors = ["Username must be unique."];
      this.emailErrors = ["Must be a valid email."];
    },
    showServerSuccessMessages() {
      this.usernameErrors = [];
      this.emailErrors = [];
      this.usernameSuccess = ["Username created!"];
      this.emailSuccess = ["Email valid!"];
    },
    clearServerSideErrors() {
      this.usernameErrors = [];
      this.emailErrors = [];
      this.usernameSuccess = [];
      this.emailSuccess = [];
    }
  },
  created() {
    let types = ["info", "success", "warning", "error"];
    types.forEach(type => {
      this.addMessage1(type);
    });
  },
  watch: {
    showBannerMessagingDialog(val) {
      if (val) {
        this.clearMessages2();
        this.addMessage2("error");
      }
    }
  }
};
</script>

<style scoped lang="scss">
select option:checked {
  display: none;
}
</style>
